import { Box, Grid2, Paper } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Vec3 } from "../../models/SyntheticMap";
import { constrain, normalizeAngleToPi } from "../../utils/spatial";
import * as THREE from "three";
import {
  BehaviorDescriptor,
  NavMode,
  RobotTwist,
} from "../../api/robocore/dex_interfaces";
import {
  Place,
  SportsEsports,
  HdrAuto,
  Explore,
  Person,
  Wifi2Bar,
  ArrowDownward,
  ArrowUpward,
  ZoomOutMap,
  SwitchVideo,
  Bolt,
  GridGoldenratio,
  Close,
} from "@mui/icons-material";
import { MapChannel } from "../../api/robocore/MapChannel";
import { dgramHas } from "../../utils/reflection";
import RCButton from "./RCButton";
import { showSnackbar } from "../../components/UniversalDialog";
import Joystick from "./Joystick";
import { RobotAgent, RobotConnectionState } from "../worldpanelparts/robot/RobotAgent";
import { AgentEvents } from "../worldpanelparts/agents/BaseAgent";
import { ModelBasedVisual } from "../../visuals/ModelBasedVisual";
import { copyVec3 } from "../../visuals/visual-utils";
import { ModelPart } from "../../models/Model";

let messageTwist: RobotTwist = {
  linear: [0, 0, 0],
  angular: [0, 0, 0],
};

export function RCPanel({ robot }: { robot: RobotAgent }) {
  const remoteControlDiv = useRef<HTMLDivElement>(null);
  const [twist, setTwist] = useState<RobotTwist>({
    linear: [0, 0, 0],
    angular: [0, 0, 0],
  });
  // const [messageTwist, setMessageTwist] = useState<RobotTwist>({
  //   linear: [0, 0, 0],
  //   angular: [0, 0, 0],
  // });
  const [maxSpeed, setMaxSpeed] = useState<RobotTwist>({
    linear: [1, 0.5, 0],
    angular: [0, 0, 1.5],
  });
  const [navMode, setNavMode] = useState<NavMode>(NavMode.Unknown);
  const [isInFocus, setIsInFocus] = useState(false);
  const [behavior, setBehavior] = useState<BehaviorDescriptor>({
    name: "",
    params: {},
  });
  const [sendingSpeeds, setSendingSpeeds] = useState(false);
  const [autoMemorize, setAutoMemorize] = useState(false);
  const [connectionState, setConnectionState] = useState(RobotConnectionState.Disconnected);
  const [suspendLocationUpdates, setSuspendLocationUpdates] = useState(false);
  const [postureMode, setPostureMode] = useState("");
  const [availableCameras, setAvailableCameras] = useState<string[]>(robot.availableCameras || []);

  useEffect(() => {
    if (!robot) {
      return;
    }
    const listener = (dgram: Partial<RobotAgent>) => {
      if (!dgram) {
        return;
      }
      if (dgram.robotTwist) {
        // console.log("Twist updated:", dgram.robotTwist);
        setTwist({ ...dgram.robotTwist });
      }

      if (dgram.connectionState) {
        setConnectionState(dgram.connectionState);
        console.log("Robot connection state:", dgram.connectionState);
        if (dgram.connectionState === RobotConnectionState.Connected) {
          setTimeout(() => {
            robot.controlChannel.sendRequest({ availableCameras: [] });
            console.log("REQUESTING AVAILABLE CAMERAS");
          }, 2000);
        }
      }

      if (dgram.message) {
        showSnackbar(dgram.message, "info");
      }

      if (dgram.navMode) {
        setNavMode(dgram.navMode);
      }

      if (dgram.behavior) {
        setBehavior(dgram.behavior);
      }
      if (dgram.postureMode) {
        // console.log("Posture mode changed:", dgram.postureMode);
        setPostureMode(dgram.postureMode);
      }
      if (dgram.availableCameras) {
        console.log("Available cameras changed:", dgram);
        setAvailableCameras(dgram.availableCameras);
      }

      if (dgramHas(dgram, { suspendLocationUpdates: true })) {
        setSuspendLocationUpdates(dgram.suspendLocationUpdates!);
      }

      if (dgram.manifest) {
        if (dgram.manifest.cfg && dgram.manifest.cfg.teleop && dgram.manifest.cfg.teleop.speedLimit) {
          const speedLimit = dgram.manifest.cfg.teleop.speedLimit;
          setMaxSpeed(speedLimit);
        }
      }
    };
    setTwist(robot.robotTwist);
    setNavMode(robot.navMode);
    setBehavior(robot.behavior);

    robot.on(AgentEvents.StateChange, listener);
    setConnectionState(robot.connectionState);
    if (robot.manifest.cfg && robot.manifest.cfg.teleop && robot.manifest.cfg.teleop.speedLimit) {
      const speedLimit = robot.manifest.cfg.teleop.speedLimit;
      setMaxSpeed(speedLimit);
    }
    // let intervalId = setInterval(() => {
    //   robot.updateTwist(messageTwist);
    // }, 300);
    return () => {
      // clearInterval(intervalId);
      robot.off(AgentEvents.StateChange, listener);
    };
  }, [robot]);
  const intervalRef = useRef<any>();
  useEffect(() => {
    if (isInFocus && sendingSpeeds) {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => {
          robot.updateTwist(messageTwist);
        }, 100);
      }
    } else if (intervalRef.current) {
      robot.updateTwist({ linear: [0, 0, 0], angular: [0, 0, 0] });
      // keep sending a few times to ensure robot receives the stop command
      setTimeout(() => {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }, 500);
    }
  }, [isInFocus, sendingSpeeds]);

  function incrementTwist(
    linearIncrement: Partial<Vec3> | null,
    angularIncrement: Partial<Vec3> | null
  ) {
    setSendingSpeeds(true);
    const [maxLinearX, maxLinearY, maxLinearZ] = maxSpeed.linear;
    const [maxAngularX, maxAngularY, maxAngularZ] = maxSpeed.angular;
    const twist = messageTwist;
    let [vx, vy, vz] = twist.linear;
    let [ax, ay, az] = twist.angular;
    if (linearIncrement) {
      vx += linearIncrement.x || 0;
      vy += linearIncrement.y || 0;
      vz += linearIncrement.z || 0;
      vx = constrain(vx, -maxLinearX, maxLinearX);
      vy = constrain(vy, -maxLinearY, maxLinearY);
      vz = constrain(vz, -maxLinearZ, maxLinearZ);
    }
    if (angularIncrement) {
      ax += angularIncrement.x || 0;
      ay += angularIncrement.y || 0;
      az += angularIncrement.z || 0;
      ax = constrain(ax, -maxAngularX, maxAngularX);
      ay = constrain(ay, -maxAngularY, maxAngularY);
      az = constrain(az, -maxAngularZ, maxAngularZ);
    }
    messageTwist.linear[0] = vx;
    messageTwist.linear[1] = vy;
    messageTwist.linear[2] = vz;
    messageTwist.angular[0] = ax;
    messageTwist.angular[1] = ay;
    messageTwist.angular[2] = az;
  }

  function resetTwist(
    linear: Partial<Vec3> | null,
    angular: Partial<Vec3> | null
  ) {
    const t = { ...twist };
    if (linear) {
      let l: Vec3 = {
        x: t.linear[0],
        y: t.linear[1],
        z: t.linear[2],
        ...linear,
      };
      t.linear = [l.x, l.y, l.z];
    }
    if (angular) {
      let a: Vec3 = {
        x: t.angular[0],
        y: t.angular[1],
        z: t.angular[2],
        ...angular,
      };
      t.angular = [a.x, a.y, a.z];
    }
    // robot.updateTwist(t);
    messageTwist = t;
    if (messageTwist.linear[0] === 0 && messageTwist.linear[1] === 0 && messageTwist.linear[2] === 0 &&
      messageTwist.angular[0] === 0 && messageTwist.angular[1] === 0 && messageTwist.angular[2] === 0) {
      setSendingSpeeds(false);
    }
  }

  function onKeyDown(e: any) {
    const angularIncrement = 0.1;
    const linearIncrement = 0.07;
    if (e.key === "q") {
      incrementTwist(null, { z: angularIncrement }); // Rotate left
    }
    if (e.key === "e") {
      incrementTwist(null, { z: -angularIncrement }); // Rotate right
    }
    if (e.key === "w") {
      incrementTwist({ x: linearIncrement }, null); // Move forward (up)
    }
    if (e.key === "s") {
      incrementTwist({ x: -linearIncrement }, null); // Move backward (down)
    }
    if (e.key === "a") {
      incrementTwist({ y: linearIncrement }, null);
    }
    if (e.key === "d") {
      incrementTwist({ y: -linearIncrement }, null);
    }
    if (robot.isDrone()) {
      if (e.key === "ArrowUp") {
        incrementTwist({ z: linearIncrement }, null);
      }
      if (e.key === "ArrowDown") {
        incrementTwist({ z: -linearIncrement }, null);
      }
    }
  }

  function onKeyUp(e: any) {
    if (e.key === "q" || e.key === "e") {
      resetTwist(null, { z: 0 });
    }
    if (e.key === "w" || e.key === "s") {
      resetTwist({ x: 0 }, null);
    }
    if (e.key === "a" || e.key === "d") {
      resetTwist({ y: 0 }, null);
    }
    if (robot.isDrone()) {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        resetTwist({ z: 0 }, null);
      }
    }
  }

  const onFocus = () => {
    setIsInFocus(true);
  };

  const onBlur = () => {
    setIsInFocus(false);
  };

  const mapChannel = robot.connection.getChannel("map") as MapChannel;

  return (
    <Box
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      tabIndex={0}
      ref={remoteControlDiv}
      style={{
        outline: "none",
        width: "100%",
        position: "relative",
      }}
      zIndex={1}
    >
      <Paper
        style={{
          margin: "5px",
          display: "inline-block",
          maxWidth: "100%",
          borderRadius: "100px",
          width: "480px",
          height: "105px",
          boxShadow: "0 0 10px rgba(0, 0, 0, 0.2)",
          backgroundColor: isInFocus ? "#222" : "#111",
        }}
      >
        <Grid2
          container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "7px",
          }}
        >
          <Joystick
            position={{ x: -twist.linear[1], y: twist.linear[2] }}
            max={{ x: maxSpeed.linear[1], y: maxSpeed.linear[2] }}
          />

          <Grid2
            sx={{
              display: "flex",
              flexWrap: "wrap",
              width: "260px",
              justifyContent: "center",
            }}
          >
            <RCButton
              tooltipTitle="Set Destination"
              onClick={() => robot.createNavGoal()}
              IconComponent={Place}
            />
            <RCButton
              tooltipTitle="Manual"
              onClick={async () => {
                await robot.switchNavMode(NavMode.Manual);
              }}
              disabled={navMode === NavMode.Manual}
              IconComponent={SportsEsports}
            />
            <RCButton
              tooltipTitle="Auto"
              onClick={async () => {
                // const m = await robot.mapBuilder.build();
                // await robot.mapBuilder.previewMap(m, true, 20000);
                robot.switchNavMode(NavMode.Auto);
              }}
              disabled={navMode === NavMode.Auto}
              IconComponent={HdrAuto}
            />
            <RCButton
              tooltipTitle="SLAM"
              onClick={async () => {
                await robot.switchNavMode(NavMode.SLAM);
              }}
              disabled={navMode === NavMode.SLAM}
              IconComponent={Explore}
            />
            <RCButton
              tooltipTitle="Gesture"
              onClick={() => {
                robot.controlChannel.sendRequest({ postureMode: 'gesture' });
              }}
              // customColor={behavior.name === 'companion' ? 'red' : 'white'}
              IconComponent={Person}
            />
            <RCButton tooltipTitle="Attack" onClick={async () => {

            }}
              customColor={behavior.name === 'attack' ? 'red' : 'white'}
              IconComponent={Bolt}
            />
            <RCButton
              tooltipTitle="Move to Initial Pose"
              onClick={() => {
                // toggleRobotLocationUpdates(remoteControlRobot, suspendLocationUpdates);
                // setSuspendLocationUpdates(!suspendLocationUpdates);
              }}
              IconComponent={ZoomOutMap}
              customColor={suspendLocationUpdates ? "red" : "white"}
            />
            <RCButton
              tooltipTitle="Connection Status"
              onClick={async () => {
                if (connectionState === RobotConnectionState.Connected) {
                  await robot.disconnect();
                } else {
                  await robot.connect();
                }
                // initializeConnectionStatus(remoteControlRobot, connectionState);
              }}
              IconComponent={Wifi2Bar}
              customColor={connectionState === RobotConnectionState.Connected ? "green" : "white"}
            />
            <RCButton
              tooltipTitle="Robot Posture"
              onClick={() => {
                const newMode = postureMode === "stand_down" ? "walk_ready" : "stand_down";
                robot.controlChannel.sendRequest({ postureMode: newMode });
              }}
              IconComponent={postureMode === "stand_down" ? ArrowUpward : ArrowDownward}
              inactiveColor="white"
            />
            <RCButton
              tooltipTitle="Switch Cameras"
              disabled={availableCameras.length < 2}
              onClick={() => {
                const selected = robot.selectedCamera;
                console.log({ selected, availableCameras });
                const selectedIndex = availableCameras.indexOf(selected);
                const nextIndex = selectedIndex < availableCameras.length - 1 ? selectedIndex + 1 : 0;
                const nextSelected = availableCameras[nextIndex];
                robot.controlChannel.sendRequest({ selectedCamera: nextSelected });
              }}
              IconComponent={SwitchVideo}
              inactiveColor="white"
            />
            <RCButton
              tooltipTitle="Grid Search"
              onClick={() => {
                // if (navMode !== NavMode.Auto) {
                //   return;
                // }
                const visuals = Array.from(robot.visual.wr.dataService.visuals.values());
                const target = visuals.find(v => v.id === 'Phenom') as ModelBasedVisual;
                if (!target) {
                  return;
                }
                const model = target.modelLoader.model!;
                const updatedParts = model.parts.map(part => {
                  const p: ModelPart = {
                    position: { x: 0, y: 0, z: 0 },
                    id: part.id,
                    rotation: { x: 0, y: 0, z: 0 },
                    parent: part.parent
                  };
                  copyVec3(part.position, p.position);
                  copyVec3(part.rotation, p.rotation);
                  // p.position.x = part.position.x;
                  p.rotation.z = normalizeAngleToPi(p.rotation.z + target.rotation.z);
                  const pos = new THREE.Vector3(part.position.x, part.position.y, part.position.z);
                  pos.applyEuler(new THREE.Euler(0, 0, target.rotation.z));
                  pos.add(new THREE.Vector3(target.position.x, target.position.y, target.position.z));
                  copyVec3(pos, p.position);
                  return p;
                });
                const poses = updatedParts.filter(p => p.id.includes('_'));
                const inspectPoints = poses.map(p => {
                  const [n, pName] = p.id.split('_');
                  const inspect = {
                    pose: p,
                    point: updatedParts.find(part => part.id === n)
                  };
                  return inspect;
                });
                const inspectionGroup = new THREE.Group();
                robot.visual.wr.dataService.activeSite!.obj.add(inspectionGroup);
                for (const ip of inspectPoints) {
                  const axesHelper = new THREE.AxesHelper(2.1);
                  axesHelper.position.set(ip.pose.position.x, ip.pose.position.y, ip.pose.position.z);
                  axesHelper.position.z += 0.1;
                  axesHelper.rotation.set(ip.pose.rotation.x, ip.pose.rotation.y, ip.pose.rotation.z);
                  inspectionGroup.add(axesHelper);
                }
                setTimeout(() => {
                  inspectionGroup.removeFromParent();
                }, 6000);
                robot.controlChannel.send({
                  request: {
                    behaviorRequest: {
                      name: 'inspect', params: { inspectPoints }
                    }
                  }
                });
              }}
              IconComponent={GridGoldenratio}
              customColor={behavior.name === 'grid_search' ? 'red' : 'white'}
            />
            <RCButton
              tooltipTitle="Save Person"
              // onClick={() => remoteControlRobot.disableActorState('Remote Control')}
              IconComponent={Close}
              customColor={autoMemorize ? 'red' : 'white'}
            />

          </Grid2>
          <Joystick
            position={{ x: -twist.angular[2], y: twist.linear[0] }}
            max={{ x: maxSpeed.angular[2], y: maxSpeed.linear[0] }}
          />
        </Grid2>
      </Paper>
    </Box>
  );
}
